import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Brain, Zap, Trophy, Clock, Flag, Check, X, Flame, Gem, Play,
  Globe, Sparkles, ChevronRight, Target,
} from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { Avatar, RankBadge, SkeletonList, Empty, SectionHead } from '../components/ui';

export default function Trivia() {
  const qc = useQueryClient();
  const { user, city } = useAuth();
  const [round, setRound] = useState(null);
  const [question, setQuestion] = useState(null);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [summary, setSummary] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [score, setScore] = useState({ correct: 0, wrong: 0, streak: 0 });
  const timerRef = useRef();

  const { data: status, isLoading } = useQuery({
    queryKey: ['triviaStatus'], queryFn: () => api.get('/trivia/status').then(r => r.data),
  });
  const { data: lb } = useQuery({
    queryKey: ['triviaLb'], queryFn: () => api.get('/trivia/leaderboard').then(r => r.data),
    enabled: !round,
  });
  const { data: history } = useQuery({
    queryKey: ['triviaHistory'], queryFn: () => api.get('/trivia/history').then(r => r.data),
    enabled: !round,
  });

  /* ---------- timer ---------- */
  useEffect(() => {
    if (!question || feedback) { clearInterval(timerRef.current); return; }
    setTimeLeft(status?.config?.seconds || 20);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); answer(null); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [question?.id, feedback]);

  const startM = useMutation({
    mutationFn: () => api.post('/trivia/start'),
    onSuccess: ({ data }) => {
      setRound(data.roundId); setQuestion(data.question);
      setScore({ correct: 0, wrong: 0, streak: 0 }); setSummary(null); setFeedback(null); setSelected(null);
      if (data.resumed) { toast('Resuming your round…', { icon: '⏱️' }); loadCurrent(data.roundId); }
    },
    onError: e => toast.error(e.message),
  });

  async function loadCurrent(id) {
    const { data } = await api.get(`/trivia/round/${id}`);
    if (data.finished) { setRound(null); setQuestion(null); return; }
    setQuestion(data.question);
    setScore({ correct: data.correct, wrong: data.wrong, streak: data.streak });
  }

  async function answer(idx) {
    if (feedback) return;
    clearInterval(timerRef.current);
    setSelected(idx);
    try {
      const { data } = await api.post(`/trivia/round/${round}/answer`, { chosen_index: idx });
      setFeedback(data);
      setScore({ correct: data.score.correct, wrong: data.score.wrong, streak: data.streak });

      if (data.correct) {
        confettiBurst(data.streak >= 3 ? 'big' : 'small');
        if (data.streak >= 3) toast.success(`🔥 ${data.streak} in a row!`, { duration: 1500 });
      }

      setTimeout(() => {
        setFeedback(null); setSelected(null);
        if (data.finished) {
          setSummary(data.summary); setQuestion(null); setRound(null);
          useAuth.getState().load();
          qc.invalidateQueries(['triviaStatus', 'triviaLb', 'triviaHistory']);
          if (data.summary.correct === data.summary.total) confettiBurst('mega');
        } else setQuestion(data.nextQuestion);
      }, data.correct ? 1700 : 2600);
    } catch (e) { toast.error(e.message); setSelected(null); }
  }

  function confettiBurst(size) {
    const n = size === 'mega' ? 90 : size === 'big' ? 40 : 18;
    const wrap = document.createElement('div');
    wrap.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden';
    const colors = ['#f97316', '#8b5cf6', '#10b981', '#fbbf24', '#ef4444', '#3b82f6'];
    for (let i = 0; i < n; i++) {
      const p = document.createElement('div');
      const c = colors[i % colors.length];
      const sz = 6 + Math.random() * 8;
      p.style.cssText = `position:absolute;left:${45 + Math.random() * 10}%;top:40%;width:${sz}px;height:${sz}px;
        background:${c};border-radius:${Math.random() > .5 ? '50%' : '2px'};
        transform:translate(-50%,-50%);opacity:1`;
      wrap.appendChild(p);
      const ang = Math.random() * Math.PI * 2;
      const vel = 120 + Math.random() * 260;
      p.animate([
        { transform: 'translate(-50%,-50%) rotate(0deg)', opacity: 1 },
        { transform: `translate(${Math.cos(ang) * vel - 50}%, ${Math.sin(ang) * vel + 260}%) rotate(${Math.random() * 720}deg)`, opacity: 0 },
      ], { duration: 900 + Math.random() * 700, easing: 'cubic-bezier(.2,.7,.3,1)' });
    }
    document.body.appendChild(wrap);
    setTimeout(() => wrap.remove(), 2000);
  }

  if (isLoading) return <SkeletonList n={3} h="h-40" />;

  /* ================= DISABLED ================= */
  if (!status?.enabled) {
    return (
      <div className="space-y-4 animate-fade-up">
        <div className="card p-6 text-center">
          <Brain size={40} className="mx-auto text-ink-300 mb-3" />
          <h1 className="font-display text-xl font-extrabold">Trivia is off here</h1>
          <p className="text-sm text-ink-500 mt-1 max-w-xs mx-auto">
            {status?.reason || 'Trivia has not been enabled for your city yet.'}
          </p>
          <Link to="/groups" className="btn-primary btn-sm mt-4">Explore other cities</Link>
        </div>
      </div>
    );
  }

  /* ================= IN GAME ================= */
  if (question) {
    const pct = (timeLeft / (status.config.seconds || 20)) * 100;
    return (
      <div className="max-w-lg mx-auto space-y-3 animate-fade-up">
        {/* HUD */}
        <div className="card p-3 bg-gradient-to-r from-grape-600 to-indigo-700 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-black opacity-80">
              QUESTION {question.index + 1}/{question.total}
            </span>
            <div className="flex gap-1.5 text-[11px] font-black">
              <span className="bg-white/20 px-2 py-0.5 rounded-lg">✅ {score.correct}</span>
              <span className="bg-white/20 px-2 py-0.5 rounded-lg">❌ {score.wrong}</span>
              {score.streak > 1 && <span className="bg-brand-500 px-2 py-0.5 rounded-lg animate-pulse">🔥 {score.streak}</span>}
            </div>
          </div>
          <div className="h-2 rounded-full bg-white/20 overflow-hidden">
            <motion.div className={`h-full rounded-full ${pct < 30 ? 'bg-red-400' : pct < 60 ? 'bg-amber-400' : 'bg-mint-400'}`}
              animate={{ width: `${pct}%` }} transition={{ duration: 0.9, ease: 'linear' }} />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="chip bg-white/20 text-white">{question.difficulty}</span>
            <span className={`text-sm font-black tabular-nums ${timeLeft <= 5 ? 'text-red-300 animate-pulse' : ''}`}>
              <Clock size={12} className="inline" /> {timeLeft}s
            </span>
            <span className="text-[10px] font-bold opacity-70 truncate max-w-[100px]">{question.topic}</span>
          </div>
        </div>

        {/* QUESTION */}
        <motion.div key={question.id} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          className="card p-5">
          <p className="font-display text-lg font-extrabold leading-snug text-balance">{question.question}</p>
        </motion.div>

        {/* OPTIONS */}
        <div className="space-y-2">
          {question.options.map((opt, i) => {
            const isSel = selected === i;
            const isCorrect = feedback && i === feedback.correct_index;
            const isWrong = feedback && isSel && !feedback.correct;
            return (
              <motion.button key={i} onClick={() => answer(i)} disabled={!!feedback}
                initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                whileTap={{ scale: 0.97 }}
                className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 text-left font-bold text-sm transition-all
                  ${isCorrect ? 'border-mint-500 bg-mint-400/15 text-mint-700'
                    : isWrong ? 'border-red-500 bg-red-50 text-red-700 animate-shake'
                    : isSel ? 'border-brand-500 bg-brand-50'
                    : 'border-ink-200 bg-white hover:border-brand-300'}`}>
                <span className={`w-8 h-8 rounded-xl grid place-items-center font-black text-xs shrink-0
                  ${isCorrect ? 'bg-mint-500 text-white' : isWrong ? 'bg-red-500 text-white' : 'bg-ink-100 text-ink-500'}`}>
                  {isCorrect ? <Check size={15} /> : isWrong ? <X size={15} /> : 'ABCD'[i]}
                </span>
                <span className="flex-1">{opt}</span>
              </motion.button>);
          })}
        </div>

        {/* FEEDBACK */}
        <AnimatePresence>
          {feedback && (
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className={`card p-4 ${feedback.correct ? 'bg-mint-400/12 border-mint-400' : 'bg-red-50 border-red-300'}`}>
              <p className={`font-display font-extrabold ${feedback.correct ? 'text-mint-700' : 'text-red-600'}`}>
                {feedback.correct ? `✅ Correct!${feedback.streak > 1 ? ` 🔥 ${feedback.streak} streak` : ''}`
                  : feedback.timedOut ? '⏰ Time is up!' : '❌ Not quite'}
              </p>
              {feedback.explanation && <p className="text-sm text-ink-600 mt-1">{feedback.explanation}</p>}
            </motion.div>)}
        </AnimatePresence>

        <div className="flex gap-2">
          <button onClick={() => api.post(`/trivia/question/${question.id}/report`).then(() => toast('Reported. Thanks!'))}
            className="btn-ghost btn-sm flex-1 text-ink-400"><Flag size={12} /> Report question</button>
          <button onClick={() => { if (confirm('Abandon this round? You lose your progress.')) { api.post(`/trivia/round/${round}/abandon`); setRound(null); setQuestion(null); } }}
            className="btn-ghost btn-sm flex-1 text-red-500">Quit</button>
        </div>
      </div>
    );
  }

  /* ================= SUMMARY ================= */
  if (summary) {
    const perfect = summary.correct === summary.total;
    return (
      <div className="max-w-lg mx-auto space-y-4 animate-fade-up">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className={`card p-7 text-center text-white ${perfect ? 'bg-gradient-to-br from-amber-400 via-brand-500 to-grape-600'
            : summary.accuracy >= 60 ? 'bg-gradient-to-br from-mint-500 to-emerald-600' : 'bg-gradient-to-br from-ink-600 to-ink-800'}`}>
          <motion.div animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.15, 1] }} transition={{ duration: 0.8 }}>
            <Trophy size={44} className="mx-auto mb-2" />
          </motion.div>
          <h2 className="font-display text-3xl font-extrabold">
            {perfect ? 'PERFECT ROUND! 🎉' : summary.accuracy >= 60 ? 'Well played!' : 'Keep training!'}
          </h2>
          <p className="font-black text-5xl my-3">{summary.correct}<span className="text-2xl opacity-60">/{summary.total}</span></p>
          <p className="font-bold opacity-90">{summary.accuracy}% accuracy · 🔥 best streak {summary.bestStreak}</p>

          <div className="mt-5 bg-white/15 rounded-2xl p-4 text-left text-sm font-bold space-y-1.5">
            <Row l="Base reward" v={`+${summary.reward.base}`} />
            {summary.reward.streak_bonus > 0 && <Row l="🔥 Streak bonus" v={`+${summary.reward.streak_bonus}`} />}
            {summary.reward.speed_bonus > 0 && <Row l="⚡ Speed bonus" v={`+${summary.reward.speed_bonus}`} />}
            {summary.reward.perfect_bonus > 0 && <Row l="🎯 Perfect bonus" v={`+${summary.reward.perfect_bonus}`} />}
            {summary.reward.sponsor_multiplier > 1 && <Row l="🏪 Sponsor boost" v={`×${summary.reward.sponsor_multiplier}`} />}
            <div className="h-px bg-white/25 my-1" />
            <Row l="TOTAL POINTS" v={`💎 ${summary.reward.total}`} big />
            <Row l="XP gained" v={`+${summary.reward.xp}`} />
          </div>

          <div className="grid grid-cols-2 gap-2 mt-5">
            <button onClick={() => { setSummary(null); qc.invalidateQueries(['triviaStatus']); }}
              className="btn bg-white/20 text-white font-extrabold">Back</button>
            <button onClick={() => { setSummary(null); startM.mutate(); }}
              disabled={!status.canPlay || startM.isPending}
              className="btn bg-white text-ink-900 font-extrabold">Play again</button>
          </div>
        </motion.div>
      </div>
    );
  }

  /* ================= LOBBY ================= */
  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card overflow-hidden">
        <div className="p-5 bg-gradient-to-br from-grape-600 via-indigo-600 to-brand-600 text-white">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 grid place-items-center"><Brain size={24} /></div>
            <div className="flex-1">
              <h1 className="font-display text-2xl font-extrabold">City Trivia</h1>
              <p className="text-white/85 text-sm">
                {status.group ? `${status.group.handle} · ` : ''}
                <Globe size={11} className="inline" /> {status.config.languageName}
              </p>
            </div>
          </div>

          {status.sponsor && (
            <Link to={`/shop/${status.sponsor.slug}`}
              className="flex items-center gap-2 mt-3 bg-white/15 rounded-xl p-2.5">
              <Avatar src={status.sponsor.logo_url} name={status.sponsor.name} size={30} square />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black opacity-70 uppercase">Sponsored by</p>
                <p className="font-bold text-sm truncate">{status.sponsor.name}</p>
              </div>
              {status.config.sponsor_multiplier > 1 && (
                <span className="chip bg-amber-400 text-ink-900">×{status.config.sponsor_multiplier}</span>)}
            </Link>)}

          <div className="grid grid-cols-3 gap-2 mt-4">
            <HUD icon={Target} v={status.config.questions} l="Questions" />
            <HUD icon={Clock} v={`${status.config.seconds}s`} l="Per question" />
            <HUD icon={Play} v={`${status.roundsLeft}/${status.dailyLimit}`} l="Rounds left" />
          </div>
        </div>

        <div className="p-4">
          <button onClick={() => startM.mutate()} disabled={!status.canPlay || startM.isPending}
            className="btn-primary w-full py-4 text-base font-black shadow-xl">
            {startM.isPending ? 'Preparing questions…'
              : status.cooldownLeft > 0 ? `⏳ Next round in ${status.cooldownLeft}m`
              : status.roundsLeft === 0 ? '✅ Daily limit reached'
              : <><Zap size={18} /> START TRIVIA</>}
          </button>

          {/* REWARD TABLE */}
          <div className="mt-4">
            <p className="label">Reward table</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
              {status.config.reward_tiers.map(t => (
                <div key={t.correct} className="bg-ink-50 rounded-xl p-2 text-center">
                  <p className="font-black text-sm">{t.correct}<span className="text-[10px] text-ink-400">✓</span></p>
                  <p className="text-[11px] font-black text-brand-600">+{t.points}💎</p>
                </div>))}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="chip-brand">🔥 +{status.config.streak_bonus}/streak</span>
              <span className="chip-mint">⚡ up to +{status.config.speed_bonus_max} speed</span>
              <span className="chip-grape">🎯 +{status.config.perfect_bonus} perfect</span>
              <span className="chip-ink">⭐ +{status.config.xp_per_correct} XP each</span>
            </div>
          </div>

          {status.config.topics?.length > 0 && (
            <div className="mt-3">
              <p className="label">Topics</p>
              <div className="flex flex-wrap gap-1.5">
                {status.config.topics.map(t => <span key={t} className="chip-ink">{t}</span>)}
              </div>
            </div>)}
        </div>
      </div>

      {status.myStats && (
        <div className="card p-4">
          <SectionHead title="Your trivia stats" sub={status.group?.handle} />
          <div className="grid grid-cols-4 gap-2">
            <S v={status.myStats.rounds} l="Rounds" />
            <S v={status.myStats.total_correct} l="Correct" />
            <S v={status.myStats.best_streak} l="Best 🔥" />
            <S v={compact(status.myStats.points_earned)} l="Points" />
          </div>
        </div>)}

      {lb?.rows?.length > 0 && (
        <div>
          <SectionHead title={`Trivia ranking${lb.group ? ` · ${lb.group.handle}` : ''}`} />
          <div className="card p-2 space-y-1">
            {lb.rows.slice(0, 15).map(u => (
              <Link key={u.user_id} to={`/u/${u.username}`}
                className={`flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50 ${u.user_id === user.id ? 'bg-brand-50' : ''}`}>
                <RankBadge rank={u.rank} />
                <Avatar src={u.image_url || u.avatar_url} name={u.username} size={36} square />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{u.hero_name || u.username}</p>
                  <p className="text-[11px] text-ink-400 font-semibold">
                    {u.accuracy}% acc · 🔥{u.best_streak} · {u.perfect_rounds} perfect
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-black text-sm text-grape-600">{u.total_correct}</p>
                  <p className="text-[9px] font-bold text-ink-400">CORRECT</p>
                </div>
              </Link>))}
          </div>
        </div>)}

      {history?.length > 0 && (
        <div>
          <SectionHead title="Recent rounds" />
          <div className="card p-2 space-y-1">
            {history.slice(0, 8).map(h => (
              <div key={h.id} className="flex items-center gap-3 p-2.5 rounded-xl">
                <div className={`w-9 h-9 rounded-xl grid place-items-center font-black text-xs text-white
                  ${h.correct === h.total ? 'bg-amber-500' : h.correct >= h.total * 0.6 ? 'bg-mint-500' : 'bg-ink-400'}`}>
                  {h.correct}/{h.total}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{h.group_handle || 'Global'}</p>
                  <p className="text-[11px] text-ink-400 font-semibold">
                    🔥{h.best_streak} · {new Date(h.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className="chip-brand">+{h.points_awarded}💎</span>
              </div>))}
          </div>
        </div>)}
    </div>
  );
}

function HUD({ icon: Icon, v, l }) {
  return (
    <div className="bg-white/15 rounded-xl p-2 text-center backdrop-blur">
      <Icon size={13} className="mx-auto mb-0.5 opacity-70" />
      <p className="font-black text-sm">{v}</p>
      <p className="text-[9px] font-bold opacity-70 uppercase">{l}</p>
    </div>
  );
}
function S({ v, l }) {
  return <div className="bg-ink-50 rounded-xl p-2 text-center">
    <p className="font-black text-base">{v}</p><p className="text-[9px] font-bold text-ink-400 uppercase">{l}</p></div>;
}
function Row({ l, v, big }) {
  return <div className={`flex justify-between ${big ? 'text-lg font-black' : ''}`}><span>{l}</span><span>{v}</span></div>;
}